import { ReactNode, Suspense, createContext, lazy, useContext, useEffect, useState } from 'react'
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { useColorMode } from '@chakra-ui/react'
import AppUrlListener from './AppUrlListener.tsx'
import ForceUpdateModal from './components/modals/ForceUpdateModal'
import AdminWrapper from './components/wrappers/AdminWrapper'
import AuthWrapper from './components/wrappers/AuthWrapper'
import UserWrapper from './components/wrappers/UserWrapper'
import useAuthentication from './hooks/useAuth'
import IndexPage from './pages/Index'
import LoadingPage from './pages/Loading'

//import { useMutation } from 'react-query'

//import { /*patchRegistration,*/ putLocation } from './api/user/home/home'

import { setBackForwardNavigationGestures } from 'capacitor-plugin-ios-webview-configurator'
import { triggerHealthKitSync } from './hooks/useHealthKitSync'
import { SwipeBack, isIOS } from './native/SwipeBack'

//import { getMessaging, getToken } from "firebase/messaging";

const LoginPage = lazy(() => import('./pages/auth/Login'))
const RegisterPage = lazy(() => import('./pages/auth/Register'))
const PasswordResetPage = lazy(() => import('./pages/auth/PasswordReset'))

const EventCheckout = lazy(() => import('./pages/checkout/EventCheckout.tsx'))
const BookCheckout = lazy(() => import('./pages/checkout/BookCheckout.tsx'))
const PackCheckout = lazy(() => import('./pages/checkout/PackCheckout.tsx'))
const EventSuccess = lazy(() => import('./pages/checkout/EventSuccess.tsx'))
const BookSuccess = lazy(() => import('./pages/checkout/BookSuccess.tsx'))

const UserHomePage = lazy(() => import('./pages/user/Home'))
const UserPlayPage = lazy(() => import('./pages/user/Play'))
const UserPlayExpandPage = lazy(() => import('./pages/user/PlayExpand'))
const UserPlayEventPage = lazy(() => import('./pages/user/PlayEvent'))
const UserEventSharePage = lazy(() => import('./pages/user/EventShare'))
const EventRedirect = lazy(() => import('./pages/user/EventRedirect'))

//const UserPlayCheckoutPage = lazy(() => import ('./pages/user/PlayCheckout'))
//const UserPlayCheckoutSuccessPage = lazy(() => import ('./pages/user/PlayCheckoutSuccess'))
//const UserPlayCheckoutErrorPage = lazy(() => import ('./pages/user/PlayCheckoutError'))
const UserCommunityPage = lazy(() => import('./pages/user/Community'))
const UserMessagesPage = lazy(() => import('./pages/user/Messages'))
const UserProShopPage = lazy(() => import('./pages/user/ProShop'))
const UserProfilePage = lazy(() => import('./pages/user/Profile'))
const UserFollowersPage = lazy(() => import('./pages/user/Followers'))

//const UserPackCheckout = lazy(() => import ('./pages/user/PackCheckout'))
//const UserPackCheckoutSuccess = lazy(() => import ('./pages/user/PackCheckoutSuccess'))
//const UserPackCheckoutError = lazy(() => import ('./pages/user/PackCheckoutError'))

//const UserPackageCheckout = lazy(() => import ('./pages/user/PackageCheckout'))
const UserPackageCheckoutSuccess = lazy(() => import('./pages/checkout/PackageCheckoutSuccess.tsx'))

const UserNotificationsPage = lazy(() => import('./pages/user/Notifications'))
const UserUpcomingEventsPage = lazy(() => import('./pages/user/UpcomingEvents'))
const UserNotificationEventSwitchSuccess = lazy(() => import('./pages/user/NotificationEventSwitchSuccess'))
const UserSettingsPage = lazy(() => import('./pages/user/Settings'))

const UserBecomeHostPage = lazy(() => import('./pages/user/BecomeHost'))

const AdminAdminsPage = lazy(() => import('./pages/admin/Admins'))
const AdminVenuesPage = lazy(() => import('./pages/admin/Venues'))
const AdminEventsPage = lazy(() => import('./pages/admin/Events'))
const AdminPackagesPage = lazy(() => import('./pages/admin/Packages'))
const AdminNotificationsPage = lazy(() => import('./pages/admin/Notifications'))
const AdminPromotionsPage = lazy(() => import('./pages/admin/Promotions'))
const AdminHostsPage = lazy(() => import('./pages/admin/Hosts'))
const AdminUsersPage = lazy(() => import('./pages/admin/Users'))
const AdminAnalyticsPage = lazy(() => import('./pages/admin/Analytics'))
const AdminPurchasesPage = lazy(() => import('./pages/admin/Purchases'))

const HostEventsPage = lazy(() => import('./pages/host/Events'))
const HostCreateEventPage = lazy(() => import('./pages/host/CreateEvent'))
const HostCreateEditLocationPage = lazy(() => import('./pages/host/CreateEditLocation'))
const HostCreateEventPageSuccess = lazy(() => import('./pages/host/CreateEventSuccess'))
const HostEditEventPage = lazy(() => import('./pages/host/EditEvent'))

import './App.css'

const PublicAuthRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    if (user?.is_staff && !error) {
      navigate('/admin')
      return
    }
    if (user && !error) {
      const redirectUrl = localStorage.getItem('redirectAfterLogin')
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin')
        navigate(redirectUrl)
      } else {
        navigate('/user')
      }
    }
  }, [user, error])

  useEffect(() => {
    // Always disable WKWebView's built-in back/forward gesture so it
    // doesn't compete with our custom SwipeBack plugin (which provides
    // the iOS-native parallax transition for routes and drawers).
    setBackForwardNavigationGestures(false)
  }, [])

  return (
    <AuthWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </AuthWrapper>
  )
}

const PUBLIC_USER_PATHS = ['/user', '/user/play', '/user/shop', '/user/community']

const UserRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)
  const location = useLocation()

  const isPublicPage =
    PUBLIC_USER_PATHS.includes(location.pathname) ||
    // /user/play/:id but NOT /user/play/event
    (location.pathname.startsWith('/user/play/') && !location.pathname.includes('/event'))

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  useEffect(() => {
    if (user?.is_staff && !error) {
      const currentUrl = window.location.href
      const newUrl = currentUrl.replace('user', 'admin')
      window.location.href = newUrl
      return
    }

    // Only redirect to login for protected pages
    if (!isPublicPage && (user == null || error)) {
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search)
      navigate('/auth/login')
    }
  }, [user, error, location.pathname])

  return (
    <UserWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PrivateHostRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error, isHost } = useContext(AuthContext)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  // const patchRegistrationID = useMutation(["registration"], patchRegistration, {
  //   onError : () => {
  //     console.log("Error in sending registration token")
  //   }
  // })

  useEffect(() => {
    if (user?.is_staff && !error) navigate('/admin')
    if (user == null || error) navigate('/auth/login')
    // Check if user is a host - redirect non-hosts to user home
    if (user && !error && !isHost && !user?.member_profile?.is_host) {
      navigate('/user')
    }
  }, [user, error, isHost, navigate])

  return (
    <UserWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PublicCheckoutRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    setBackForwardNavigationGestures(false)
  }, [])

  useEffect(() => {
    if (user?.is_staff && !error) navigate('/admin')
  }, [user, error])

  if (user != null) {
    return (
      <UserWrapper>
        <Suspense fallback={<div />}>{children}</Suspense>
      </UserWrapper>
    )
  }

  return (
    <>
      <Suspense fallback={<div />}>{children}</Suspense>
    </>
  )
}

const PrivateAdminRoute = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const { user, error } = useContext(AuthContext)

  useEffect(() => {
    if (!user?.is_staff && !error) navigate('/user')
    if (user == null || error) navigate('/auth/login')
  }, [user, error])

  return (
    <AdminWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </AdminWrapper>
  )
}

const Router = () => {
  const { loading } = useContext(AuthContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const received = PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received: ', notification)
      // Silent push that wakes the app to upload HealthKit samples for finished bookings.
      // Backend: event.tasks.dispatch_healthkit_sync_pushes.
      if (notification?.data?.type === 'healthkit_sync') {
        triggerHealthKitSync()
      }
    })
    const performed = PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('Push notification action performed', notification.actionId, notification.inputValue)
      console.log(`Push notification: ${notification.notification.data.link}`)
      if (notification.notification.data.link) {
        navigate(notification.notification.data.link)
      }
    })
    return () => {
      void received.then((l) => l.remove())
      void performed.then((l) => l.remove())
    }
  }, [navigate])

  useEffect(() => {
    // Check if the URL contains the redirect_to parameter
    const currentURL = window.location.href

    if (currentURL.includes('redirect_to=')) {
      // Extract the value of the redirect_to parameter
      const url = new URL(currentURL)

      const redirectFragment = url.searchParams.get('redirect_to')
      url.search = url.searchParams.toString()
      const decodedFragment = decodeURI(redirectFragment ?? '')

      window.location.hash = decodedFragment
    }
  }, [])

  if (loading) return <LoadingPage />

  return (
    <Routes>
      <Route
        path="admin/*"
        element={
          <PrivateAdminRoute>
            <Routes>
              <Route path="/admin" element={<AdminAdminsPage />} />
              <Route path="/venues" element={<AdminVenuesPage />} />
              <Route path="/venues/create" element={<HostCreateEditLocationPage />} />
              <Route path="/venues/edit/:id" element={<HostCreateEditLocationPage isEdit />} />
              <Route path="/events" element={<AdminEventsPage />} />
              <Route path="/events/create" element={<HostCreateEventPage />} />
              <Route path="/events/edit/:id" element={<HostEditEventPage />} />
              <Route path="/packages" element={<AdminPackagesPage />} />
              <Route path="/notifications" element={<AdminNotificationsPage />} />
              <Route path="/promotions" element={<AdminPromotionsPage />} />
              <Route path="/hosts" element={<AdminHostsPage />} />
              <Route path="" element={<AdminAnalyticsPage />} />

              <Route path="/users" element={<AdminUsersPage />} />
              <Route path="/purchases" element={<AdminPurchasesPage />} />

              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/community" element={<UserCommunityPage />} />
            </Routes>
          </PrivateAdminRoute>
        }
      />

      <Route
        path="auth/*"
        element={
          <PublicAuthRoute>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset" element={<PasswordResetPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Routes>
          </PublicAuthRoute>
        }
      />

      <Route
        path="checkout/*"
        element={
          <PublicCheckoutRoute>
            <Routes>
              <Route path="/play" element={<EventCheckout />} />
              <Route path="/play/success" element={<EventSuccess />} />
              {/*<Route path="/play/error" element={<UserPlayCheckoutErrorPage />} />*/}

              <Route path="/book" element={<BookCheckout />} />
              <Route path="/book/success" element={<BookSuccess />} />
              {/*<Route path="/book/error" element={<UserPackCheckoutError />} />*/}

              <Route path="/pack" element={<PackCheckout />} />
              <Route path="/pack/success" element={<UserPackageCheckoutSuccess />} />
              {/*<Route path="/pack/error" element={<UserPackCheckoutError />} />*/}
            </Routes>
          </PublicCheckoutRoute>
        }
      />

      <Route
        path="user/play/:venueId/event/:eventId"
        element={
          <PublicCheckoutRoute>
            <UserEventSharePage />
          </PublicCheckoutRoute>
        }
      />

      <Route
        path="host/*"
        element={
          <PrivateHostRoute>
            <Routes>
              <Route path="/location/create" element={<HostCreateEditLocationPage />} />
              <Route path="/location/edit/:id" element={<HostCreateEditLocationPage isEdit />} />

              <Route path="/events" element={<HostEventsPage />} />
              <Route path="/event/create/success" element={<HostCreateEventPageSuccess />} />
              <Route path="/event/create" element={<HostCreateEventPage />} />
              <Route path="/event/edit/:id" element={<HostEditEventPage />} />
            </Routes>
          </PrivateHostRoute>
        }
      />

      <Route
        path="user/*"
        element={
          <UserRoute>
            <Routes>
              <Route path="/" element={<UserHomePage />} />
              <Route path="/play" element={<UserPlayPage />} />
              {/* <Route path="/play/checkout/success" element={<UserPlayCheckoutSuccessPage />} />
          <Route path="/play/checkout/error" element={<UserPlayCheckoutErrorPage />} />
          <Route path="/play/checkout/:id" element={<UserPlayCheckoutPage />} /> */}
              <Route path="/play/:id" element={<UserPlayExpandPage />} />
              <Route path="/play/event" element={<UserPlayEventPage />} />
              <Route path="/community" element={<UserCommunityPage />} />
              <Route path="/messages" element={<UserMessagesPage />} />
              <Route path="/shop" element={<UserProShopPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/followers" element={<UserFollowersPage />} />
              <Route path="/settings/:page" element={<UserSettingsPage />} />

              {/* <Route path='/pack/checkout/:id' element={<UserPackCheckout />} />
          <Route path='/pack/checkout/success' element={<UserPackCheckoutSuccess />} />
          <Route path='/pack/checkout/error' element={<UserPackCheckoutError />} /> */}

              {/* <Route path='/package/checkout' element={<UserPackageCheckout />} /> */}
              <Route path="/package/checkout/success" element={<UserPackageCheckoutSuccess />} />
              <Route path="/events" element={<UserUpcomingEventsPage />} />
              <Route path="/notifications" element={<UserNotificationsPage />} />
              <Route path="/notifications/switch/success" element={<UserNotificationEventSwitchSuccess />} />

              <Route path="/become-a-host" element={<Navigate to="/user/become-a-coach" replace />} />
              <Route path="/become-a-coach" element={<UserBecomeHostPage />} />
            </Routes>
          </UserRoute>
        }
      />
      <Route
        path="event/:eventId"
        element={
          <Suspense fallback={<div />}>
            <EventRedirect />
          </Suspense>
        }
      />
      <Route path="*" element={<IndexPage />} />
    </Routes>
  )
}

export const AuthContext = createContext<{
  user: User | null
  loading: boolean
  error: any
  isHost: boolean
  setIsHost: (v: boolean) => void
  logout: () => void
  signInWithGoogle: () => void
  signInWithFacebook: () => void
  signInWithApple: () => void
  signInWithEmailAndPassword: (email: string, password: string) => void
  registerWithEmailAndPassword: (email: string, password: string) => void
  getAccessToken: () => Promise<string>
  loginLoading: boolean
  loginError: boolean
  isAccountDeleted: boolean
  rememberMe: boolean
  setRememberMe: (v: boolean) => void
  userLocation?: { lat: number; lng: number } // Add this line
  setUserLocation: React.Dispatch<React.SetStateAction<{ lat: number; lng: number }>> // Add this line
}>({
  user: null,
  loading: true,
  error: null,
  isHost: false,
  setIsHost: () => {},
  logout: () => {},
  signInWithGoogle: () => {},
  signInWithFacebook: () => {},
  signInWithApple: () => {},
  signInWithEmailAndPassword: () => {},
  registerWithEmailAndPassword: () => {},
  getAccessToken: () => {
    return new Promise(() => '')
  },
  loginLoading: false,
  loginError: false,
  isAccountDeleted: false,
  rememberMe: false,
  setRememberMe: () => {},
  userLocation: { lat: 34.052235, lng: -118.243683 }, // Default location
  setUserLocation: () => {}, // Default no-op function
})

const App = () => {
  const { colorMode, toggleColorMode } = useColorMode()
  if (colorMode === 'dark') toggleColorMode()

  const {
    user,
    loading,
    error,
    logout,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signInWithEmailAndPassword,
    registerWithEmailAndPassword,
    getAccessToken,
    loginLoading,
    loginError,
    isAccountDeleted,
    rememberMe,
    setRememberMe,
    showForceUpdate,
  } = useAuthentication()

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>({
    lat: 34.052235, // Default center (Los Angeles)
    lng: -118.243683,
  })

  const initialHostSettings = async () => {
    const hostSettings = await localStorage.getItem('isHost')

    setIsHost(
      hostSettings && user?.member_profile?.is_host ? hostSettings == 'true' : user?.member_profile?.is_host ?? false
    )
  }

  useEffect(() => {
    initialHostSettings()
  }, [user])

  const [isHost, setIsHost] = useState<boolean>(user?.member_profile?.is_host ?? false)

  useEffect(() => {
    if (user != null) {
      console.log('CURRENT HOST SET UP: ', isHost)
      localStorage.setItem('isHost', JSON.stringify(isHost))
    }
  }, [isHost])

  const ScrollToTop = () => {
    const { pathname } = useLocation()

    useEffect(() => {
      window.scrollTo(0, 0)
      // Reset the swipe-back snapshot stack whenever the user lands on a
      // tab-bar root. Push sites only instrument forward nav; this keeps
      // the stack from drifting across tab switches or deep links.
      if (isIOS() && PUBLIC_USER_PATHS.includes(pathname)) {
        void SwipeBack.clearSnapshots()
      }
    }, [pathname])

    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isHost,
        setIsHost,
        logout,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
        signInWithEmailAndPassword,
        registerWithEmailAndPassword,
        getAccessToken,
        loginLoading,
        loginError,
        isAccountDeleted,
        rememberMe,
        setRememberMe,
        userLocation, // Provide userLocation
        setUserLocation, // Provide setUserLocation
      }}
    >
      <HashRouter>
        <ScrollToTop />
        <AppUrlListener />
        <Router />
        <ForceUpdateModal isOpen={showForceUpdate} />
      </HashRouter>
    </AuthContext.Provider>
  )
}

export default App
